import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

import { asNr, formatAmount, parsePersonalNumber } from './format'
import BankVerify from './bankverify'
import { LoanLimits } from './loanLimits'

import {
  validateMinValue,
  validateMaxValue,
  validateInteger,
  validateStrictInteger,
  validateEmail,
  validatePhone,
  validatePersonalNumber,
  validateAgeOfUser,
  validateUuid,
} from './inputValidationUtils'

const EmploymentStatus = {
  EMPLOYED: 'employed',
  RETIRED: 'retired',
  UNEMPLOYED: 'unemployed',
  SELF_EMPLOYED: 'selfEmployed',
  TRIAL_EMPLOYED: 'trialEmployed',
  HOURLY: 'hourly',
  STUDENT: 'student',
} as const

type EmploymentStatus = (typeof EmploymentStatus)[keyof typeof EmploymentStatus]

export type ValidationRule = (value: unknown) => boolean | string | Promise<boolean | string>

export type ValidationRuleObject = {
  id: string
  rule: ValidationRule
}

const isEmpty = (value: unknown) => {
  // Special case for array of files that is a Proxy array as an object
  if (typeof value === 'object' && value !== null && Object.values(value).length === 0) return true

  if (typeof value === 'number' && value === 0) return false
  if (typeof value === 'boolean') return !value

  return value === undefined || !value
}

type CreateRuleProps = {
  name: string
  params: unknown[]
  rule: ValidationRule
}

const createRule = (props: CreateRuleProps): ValidationRuleObject => ({
  id: `${props.name}(${props.params.join(', ')})`,
  rule: props.rule,
})

/**
 * RULES
 * Name and params are required so that we can watch for updates.
 */

export const required = (options?: { message?: string; allowFalse?: boolean }) =>
  createRule({
    name: 'required',
    params: [options?.message],
    rule: (value: unknown) =>
      (options?.allowFalse ? value === false : false) ||
      !isEmpty(value) ||
      options?.message ||
      'Detta fält måste fyllas i.',
  })

export const email = (errorMessage?: string) =>
  createRule({
    name: 'email',
    params: [errorMessage],
    rule: async (value: unknown) =>
      validateEmail(String(value)) || errorMessage || 'Det här verkar inte vara en giltig e-postadress.',
  })

export const phone = (errorMessage?: string) =>
  createRule({
    name: 'phone',
    params: [errorMessage],
    rule: async (value: unknown) =>
      validatePhone(String(value)) || errorMessage || 'Det här verkar inte vara ett giltigt telefonnummer.',
  })

export const minLength = (param: number, errorMessage?: string) =>
  createRule({
    name: 'minLength',
    params: [param, errorMessage],
    rule: (value: unknown) =>
      isEmpty(value) ||
      String(value).length >= param ||
      errorMessage ||
      `Detta fält måste vara minst ${param} tecken långt.`,
  })

export const maxLength = (param: number, errorMessage?: string) =>
  createRule({
    name: 'maxLength',
    params: [param, errorMessage],
    rule: (value: unknown) =>
      isEmpty(value) ||
      String(value).length <= param ||
      errorMessage ||
      `Detta fält får max vara ${param} tecken långt.`,
  })

export const minValue = (param: number) =>
  createRule({
    name: 'minValue',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) || validateMinValue(value, param) || `Detta fält måste vara minst ${param}.`,
  })

export const maxValue = (param: number) =>
  createRule({
    name: 'maxValue',
    params: [param],
    rule: (value: unknown) => isEmpty(value) || validateMaxValue(value, param) || `Detta fält får högst vara ${param}.`,
  })

export const strictInteger = () =>
  createRule({
    name: 'strictInteger',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || validateStrictInteger(value) || `Detta fält måste innehålla ett giltigt heltal.`,
  })

export const integer = () =>
  createRule({
    name: 'integer',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || validateInteger(value) || `Detta fält måste innehålla ett giltigt heltal.`,
  })

export const exactValue = (param: unknown, errorMessage?: string) =>
  createRule({
    name: 'exactValue',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) || value === param || errorMessage || `Detta fält måste vara exakt ${param}.`,
  })

export const minimumLoanAmount = (param: number) =>
  createRule({
    name: 'minimumLoanAmount',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) || validateMinValue(value, param) || `Vårt lägsta lånebelopp är ${formatAmount(param)} kr.`,
  })

export const maximumLoanAmount = (param: number) =>
  createRule({
    name: 'maximumLoanAmount',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) || validateMaxValue(value, param) || `Vårt högsta lånebelopp är ${formatAmount(param)} kr.`,
  })

export const apartmentNumber = () =>
  createRule({
    name: 'apartmentNumber',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) ||
      !!String(value)
        .trim()
        .match(/^[0-9]{4}$/) ||
      'Här behövs det ett lägenhetsnummer med fyra siffror som tillhör adressen i flerfamiljshus.',
  })

// Plain 2-letter ISO country code, e.g. for a country picker without an options list.
export const countryCode = () =>
  createRule({
    name: 'countryCode',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || /^[A-Za-z]{2}$/.test(String(value).trim()) || 'Ange en landskod med två bokstäver, t.ex. SE.',
  })

export const personalNumber = () =>
  createRule({
    name: 'personalNumber',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) ||
      validatePersonalNumber(String(value)) ||
      'Det här verkar inte vara ett giltigt personnummer. Fyll i ditt personnummer med tolv siffror, t.ex. 19770101-1234.',
  })

export const age = (errorMessage?: string) =>
  createRule({
    name: 'age',
    params: [errorMessage],
    rule: (value: unknown) =>
      isEmpty(value) ||
      validateAgeOfUser(String(value)) ||
      errorMessage ||
      'Du måste vara minst 18 år gammal för att ansöka om lån.',
  })

export const uuid = () =>
  createRule({
    name: 'uuid',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || validateUuid(String(value).trim()) || 'Det här verkar inte vara ett giltigt UUID.',
  })

// A commitment applies for the estimated valuation minus the cash contribution, so the rule sits on the cash
// contribution and takes the estimated valuation as a param.
export const loanAmountAfterCashContribution = (valuation: number | string | undefined) =>
  createRule({
    name: 'loanAmountAfterCashContribution',
    params: [valuation],
    rule: (value: unknown) => {
      if (isEmpty(value) || isEmpty(valuation)) return true
      const loanAmount = asNr(valuation) - asNr(value)
      return (
        (loanAmount >= LoanLimits.MIN_AMOUNT && loanAmount <= LoanLimits.MAX_AMOUNT) ||
        `Lånebeloppet (värdering minus kontantinsats) blir ${formatAmount(loanAmount)} kr. Det måste vara mellan ${formatAmount(LoanLimits.MIN_AMOUNT)} och ${formatAmount(LoanLimits.MAX_AMOUNT)} kr.`
      )
    },
  })

export const disallowedPersonalNumbers = (param: string[], errorMessage?: string) =>
  createRule({
    name: 'disallowedPersonalNumbers',
    params: [param, errorMessage],
    rule: (value: unknown) =>
      isEmpty(value) ||
      !param.map((pn) => parsePersonalNumber(pn)).includes(parsePersonalNumber(String(value))) ||
      errorMessage ||
      'Personnummer tillåts inte.',
  })

// Takes an array of values that are allowed
export const allowedValues = (param: string[], errorMessage?: string) =>
  createRule({
    name: 'allowedValues',
    params: [param, errorMessage],
    rule: (value: unknown) => isEmpty(value) || param.includes(String(value)) || errorMessage || 'Ogiltigt val.',
  })

// Takes an array of values that are NOT allowed
export const disallowedValues = (param: string[], errorMessage?: string) =>
  createRule({
    name: 'disallowedValues',
    params: [param, errorMessage],
    rule: (value: unknown) => isEmpty(value) || !param.includes(String(value)) || errorMessage || 'Ogiltigt val.',
  })

// For backwards compatibility, use the new "allowedValues" rule instead
export const allowedEmploymentStatus = (
  param: EmploymentStatus[] = [
    EmploymentStatus.EMPLOYED,
    EmploymentStatus.SELF_EMPLOYED,
    EmploymentStatus.HOURLY,
    EmploymentStatus.RETIRED,
  ],
) =>
  createRule({
    name: 'allowedEmploymentStatus',
    params: [param],
    rule: (value: unknown) => isEmpty(value) || param.includes(value as EmploymentStatus) || 'Ogiltigt val.',
  })

export const bankgiro = () =>
  createRule({
    name: 'bankgiro',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) ||
      /^\d{3}[-]\d{4}$|^([5]\d{3}[-]\d{4})$/.test(String(value)) ||
      'Ange bankgiro i formatet XXX-XXXX eller 5XXX-XXXX.',
  })

export const accountNumber = () =>
  createRule({
    name: 'accountNumber',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || BankVerify.init(String(value), 'account').valid() || 'Ogiltigt kontonummer.',
  })

export const date = (param: string = 'YYYY-MM-DD') =>
  createRule({
    name: 'date',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) ||
      dayjs(String(value), param, true).isValid() ||
      `Ogiltigt datum. Fyll i datumet i formatet ${param}.`,
  })

export const time = () =>
  createRule({
    name: 'time',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) ||
      dayjs(String(value), 'HH:mm', true).format('HH:mm') === String(value) ||
      'Ogiltig tid. Fyll i tiden i formatet HH:mm.',
  })

// If no param is supplied, the date is validated against today's date
export const dateIsAfter = (param?: string) =>
  createRule({
    name: 'dateIsAfter',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) ||
      dayjs(String(value)).isAfter(dayjs(param)) ||
      `Datumet måste inträffa efter ${dayjs(param).format('YYYY-MM-DD')}.`,
  })

// If no param is supplied, the date is validated against today's date
export const dateIsBefore = (param?: string) =>
  createRule({
    name: 'dateIsBefore',
    params: [param],
    rule: (value: unknown) =>
      isEmpty(value) ||
      dayjs(String(value)).isBefore(dayjs(param)) ||
      `Datumet måste inträffa före ${dayjs(param).format('YYYY-MM-DD')}.`,
  })

export const dateIsTodayOrLater = () =>
  createRule({
    name: 'dateIsTodayOrLater',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || !dayjs(String(value)).isBefore(dayjs(), 'day') || 'Datumet kan inte vara före idag.',
  })

export const dateIsWithinRange = (start?: string, end?: string) =>
  createRule({
    name: 'dateIsWithinRange',
    params: [start, end],
    rule: (value: unknown) =>
      isEmpty(value) ||
      (dayjs(String(value)).isAfter(dayjs(start)) && dayjs(String(value)).isBefore(dayjs(end))) ||
      `Datumet måste vara efter ${dayjs(start).format('YYYY-MM-DD')} och före ${dayjs(end).format('YYYY-MM-DD')}.`,
  })

// For backwards compatibility, use the new "date", "dateIsAfter" and "dateIsBefore" rules instead
export const dateOldRule = () =>
  createRule({
    name: 'dateOldRule',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) ||
      (dayjs(String(value)).isAfter(dayjs(), 'day') && dayjs(String(value), 'YYYY-MM-DD', true).isValid()) ||
      'Ogiltigt datum.',
  })

export const numeric = () =>
  createRule({
    name: 'numeric',
    params: [],
    rule: (value: unknown) =>
      isEmpty(value) || /^[0-9]*$/.test(String(value)) || 'Vänligen fyll i fältet med endast siffror.',
  })

export const maxDepositLoan = (max: number) =>
  createRule({
    name: 'maxDepositLoan',
    params: [max],
    rule: (value: unknown) => validateMaxValue(value, max) || 'Beloppet kan inte överstiga överskottet.',
  })
